/**
 * PaginationBar — exact visual/behavioral replica of the internal `Pagination`
 * component used by `Table.tsx` (MasterCrud's pagination), which is not exported
 * by that module. Three sections: left (page-size selector + "por página" +
 * total records), center (Primera/Anterior/números/Siguiente/Última — icon-only
 * nav), right ("Ir a página" input).
 *
 * @module components/list/PaginationBar
 * @see FMPV-MVWC-E002-S004 I-002
 */
import React, { useState } from 'react';

const ChevronLeftIcon = () => (
  <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m15 18-6-6 6-6" /></svg>
);
const ChevronRightIcon = () => (
  <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m9 18 6-6-6-6" /></svg>
);
const ChevronsLeftIcon = () => (
  <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m11 17-5-5 5-5" /><path d="m18 17-5-5 5-5" /></svg>
);
const ChevronsRightIcon = () => (
  <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m6 17 5-5-5-5" /><path d="m13 17 5-5-5-5" /></svg>
);

export interface PaginationBarProps {
  currentPage: number;
  totalPages: number;
  totalRecords: number;
  pageSize: number;
  pageSizeOptions: number[];
  onPageChange: (page: number) => void;
  onPageSizeChange: (pageSize: number) => void;
  /** Translation function override — falls back to the current Spanish text when omitted. */
  t?: (key: string, defaultValue?: string) => string;
}

export function PaginationBar({
  currentPage,
  totalPages,
  totalRecords,
  pageSize,
  pageSizeOptions,
  onPageChange,
  onPageSizeChange,
  t,
}: PaginationBarProps) {
  const tt = t ?? ((key: string, defaultValue?: string) => defaultValue ?? key);
  const [goToValue, setGoToValue] = useState('');

  const multiPage = totalPages > 1;

  // Hasta 3 páginas se muestran todas; con más, una ventana de 2 alrededor de la
  // actual — primera/última se cubren con los chevrons dobles (sin puntos suspensivos).
  const getPageNumbers = (): number[] => {
    if (totalPages <= 3) {
      return Array.from({ length: totalPages }, (_, i) => i + 1);
    }
    const start = Math.min(Math.max(currentPage, 1), totalPages - 1);
    return [start, start + 1];
  };
  const pageNumbers = getPageNumbers();

  const handleGoTo = () => {
    const n = parseInt(goToValue, 10);
    if (!isNaN(n) && n >= 1 && n <= totalPages) onPageChange(n);
    setGoToValue('');
  };

  const previousLabel = tt('paginationPrev', 'Anterior');
  const nextLabel = tt('paginationNext', 'Siguiente');

  const formControlCls =
    'text-sm text-content-primary dark:text-dark-content-primary bg-white dark:bg-dark-bg-primary border border-border-primary dark:border-dark-border-primary rounded py-1 focus:outline-none focus:border-[#93d1fd] transition-colors disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer';

  const navIconBtnCls =
    'inline-flex items-center justify-center w-8 h-8 rounded-md cursor-pointer text-primary-600 dark:text-primary-600 hover:bg-primary-100 dark:hover:bg-primary-900/20 active:scale-95 transition-all duration-150 disabled:opacity-40 disabled:cursor-not-allowed';

  const navNumBtnCls = (active: boolean) =>
    `inline-flex items-center justify-center min-w-8 h-8 px-3 rounded-md cursor-pointer text-sm font-bold transition-all duration-150 ${
      active
        ? 'bg-primary-100 dark:bg-primary-900/30 text-primary-600 dark:text-primary-600'
        : 'text-primary-600 dark:text-primary-600 hover:bg-primary-100 dark:hover:bg-primary-900/20 active:scale-95'
    }`;

  const navSection = (
    <nav className="flex items-center gap-0.5" aria-label={tt('pagination', 'Paginación')} data-testid="pagination">
      <button type="button" title={previousLabel} aria-label="Primera página" onClick={() => currentPage > 1 && onPageChange(1)} disabled={!multiPage || currentPage === 1} className={navIconBtnCls}>
        <ChevronsLeftIcon />
      </button>
      <button type="button" title={previousLabel} aria-label={previousLabel} onClick={() => currentPage > 1 && onPageChange(currentPage - 1)} disabled={!multiPage || currentPage === 1} className={navIconBtnCls}>
        <ChevronLeftIcon />
      </button>

      {multiPage && pageNumbers.map((page) => (
        <button
          key={page}
          type="button"
          onClick={() => onPageChange(page)}
          aria-current={page === currentPage ? 'page' : undefined}
          className={navNumBtnCls(page === currentPage)}
        >
          {page}
        </button>
      ))}

      <button type="button" title={nextLabel} aria-label={nextLabel} onClick={() => currentPage < totalPages && onPageChange(currentPage + 1)} disabled={!multiPage || currentPage === totalPages} className={navIconBtnCls}>
        <ChevronRightIcon />
      </button>
      <button type="button" title={nextLabel} aria-label="Última página" onClick={() => currentPage < totalPages && onPageChange(totalPages)} disabled={!multiPage || currentPage === totalPages} className={navIconBtnCls}>
        <ChevronsRightIcon />
      </button>
    </nav>
  );

  return (
    <div className="flex items-center justify-between w-full gap-4 flex-wrap px-4 py-2 border-t border-border-primary dark:border-dark-border-primary">
      {/* IZQUIERDA — Selector de registros por página + total */}
      <div className="flex items-center gap-1.5 flex-none">
        <select
          value={pageSize}
          onChange={(e) => onPageSizeChange(Number(e.target.value))}
          aria-label={tt('recordsPerPage', 'Registros por página')}
          className={`${formControlCls} px-2`}
        >
          {pageSizeOptions.map((size) => (
            <option key={size} value={size}>{size}</option>
          ))}
        </select>
        <span className="text-sm text-content-tertiary dark:text-dark-content-tertiary whitespace-nowrap">
          {tt('paginationPerPage', 'por página')}
        </span>
        <span className="text-content-secondary dark:text-dark-content-secondary select-none px-0.5">·</span>
        <span className="text-sm text-content-tertiary dark:text-dark-content-tertiary whitespace-nowrap">
          {totalRecords.toLocaleString()} {tt('paginationRecords', 'registros')}
        </span>
      </div>

      {/* CENTRO — Navegación */}
      {navSection}

      {/* DERECHA — Ir a página */}
      <div className="flex items-center gap-2 flex-none">
        <span className="text-sm text-content-tertiary dark:text-dark-content-tertiary whitespace-nowrap">
          {tt('paginationGoToPage', 'Página')}
        </span>
        <input
          type="number"
          min={1}
          max={totalPages || 1}
          value={goToValue}
          onChange={(e) => setGoToValue(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') handleGoTo(); }}
          onBlur={handleGoTo}
          placeholder={String(currentPage)}
          disabled={!multiPage}
          aria-label={tt('paginationGoToPageAria', 'Ir a página')}
          className={`${formControlCls} w-16 text-center px-2`}
        />
      </div>
    </div>
  );
}
